import React, { useState } from 'react';
import ReactModal from 'react-modal';
import './CSS/AgendarConsultaModal.css';
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import clinicaConfig from "../clinicaConfig.js";

const API_BASE_URL = clinicaConfig.REACT_APP_API_BASE_URL || 'http://localhost:4000';

ReactModal.setAppElement('#root');

const onlyDigits = (s = '') => String(s).replace(/\D/g, '');
const formatPhoneDisplay = (digits) => {
  const d = onlyDigits(digits);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
};

const AgendarConsultaModal = ({ isOpen, onClose, consulta, medico }) => {
  const [form, setForm] = useState({ nome: '', telefoneDisplay: '', telefoneDigits: '', consent: false });
  const [errors, setErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleTelephoneChange = (value) => {
    const digits = onlyDigits(value);
    const limited = digits.slice(0, 11);
    setForm((prev) => ({ ...prev, telefoneDigits: limited, telefoneDisplay: formatPhoneDisplay(limited) }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'telefone') {
      handleTelephoneChange(value);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const canSave = () => {
    if (!form.nome.trim()) return false;
    if (!form.telefoneDigits || form.telefoneDigits.length < 10) return false;
    if (!form.consent) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome || !form.telefoneDigits || form.telefoneDigits.length < 10) {
      setErrors('Nome e telefone (DDD + número) são obrigatórios. Ex: 75992558190');
      return;
    }

    setIsSaving(true);
    setErrors(null);

    try {
      const rawAll = onlyDigits(form.telefoneDigits);
      const withoutCC = rawAll.startsWith('55') ? rawAll.slice(2) : rawAll;
      const sendPhone = `55${withoutCC}`;
      const whatsappId = `${sendPhone}@s.whatsapp.net`;

      const patient = {
        nome: form.nome.trim(),
        telefone: sendPhone,
        whatsappId,
        consent: form.consent
      };

      const response = await fetch(`${API_BASE_URL}/public/book-appointment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Client-Domain': window.location.hostname
        },
        body: JSON.stringify({
          consultaId: consulta._id || consulta.id,
          patient
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erro ao agendar consulta. Tente novamente.');
      }

      setShowSuccess(true);
      setForm({ nome: '', telefoneDisplay: '', telefoneDigits: '', consent: false });

    } catch (err) {
      console.error('Erro ao agendar consulta:', err);
      setErrors(err.message || 'Erro ao agendar consulta. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = () => {
    if (!consulta) return 'N/A';
    try {
      return consulta.dataConsulta ? new Date(consulta.dataConsulta).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'N/A';
    } catch (e) { return 'N/A'; }
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setForm({ nome: '', telefoneDisplay: '', telefoneDigits: '', consent: false });
      setErrors(null);
      setShowSuccess(false);
      onClose();
    }
  };

  const handleCloseSuccess = () => {
    setForm({ nome: '', telefoneDisplay: '', telefoneDigits: '', consent: false });
    setErrors(null);
    setShowSuccess(false);
    onClose();
    // Recarregar a página para atualizar a lista de consultas
    window.location.reload();
  };

  const handleWhatsAppContact = () => {
    const dataFormatada = formatDate();
    const mensagem = `Olá, acabei de agendar a minha consulta com ${medico?.genero === "Masculino" ? "Dr." : "Dra."} ${medico?.nome} para ${dataFormatada} às ${consulta?.horaConsulta}.`;
    const whatsappUrl = `https://wa.me/${clinicaConfig.whatsapp}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <ToastContainer position="bottom-right" autoClose={3000} theme="colored" />
      <ReactModal 
        isOpen={isOpen} 
        onRequestClose={handleCloseModal} 
        overlayClassName="agendar-overlay" 
        className="agendar-modal"
      >
        {!showSuccess ? (
          <>
            <h3>Agendar Consulta</h3>
            <div className="agendar-consulta-summary">
              <div><strong>Médico:</strong> {medico?.genero === "Masculino" ? "Dr." : "Dra."} {medico?.nome || 'N/A'}</div>
              <div><strong>Especialidade:</strong> {medico?.especialidade || 'N/A'}</div>
              <div><strong>Data:</strong> {formatDate()}</div>
              <div><strong>Hora:</strong> {consulta?.horaConsulta || 'N/A'}</div>
              {consulta?.valorConsulta && parseFloat(consulta.valorConsulta) > 0 && (
                <div><strong>Valor:</strong> R$ {consulta.valorConsulta},00</div>
              )}
            </div>
            <form onSubmit={handleSubmit} className="agendar-form">
              <label>Nome Completo *</label>
              <input 
                name="nome" 
                value={form.nome} 
                onChange={handleChange} 
                autoFocus 
                placeholder="Digite seu nome completo"
                disabled={isSaving}
              />

              <label className="agendar-consent">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleChange}
                  disabled={isSaving}
                />
                <span>Autorizo o uso destes dados para confirmar o agendamento e enviar lembretes pelo WhatsApp.</span>
              </label>
              
              <label>Telefone (DDD + número) *</label>
              <input 
                name="telefone" 
                value={form.telefoneDisplay} 
                onChange={handleChange} 
                placeholder="(75) 99999-9999"
                disabled={isSaving}
              />
              
              {errors && <div className="form-error">{errors}</div>}
              
              <div className="modal-buttons">
                <button 
                  type="button" 
                  className="btn cancel" 
                  onClick={handleCloseModal}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={!canSave() || isSaving} 
                  className="btn confirm"
                >
                  {isSaving ? 'Enviando...' : 'Confirmar Agendamento'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="success-message">
            <button className="close-success-btn" onClick={handleCloseSuccess} title="Fechar">
              ×
            </button>
            <div className="success-icon">✓</div>
            <h3>Consulta Confirmada!</h3>
            <p>Sua consulta foi agendada com sucesso.</p>
            <p className="success-info">
              Entraremos em contato um dia antes da consulta para confirmar os dados.
            </p>
            <div className="agendar-consulta-summary success-summary">
              <div><strong>Médico:</strong> {medico?.genero === "Masculino" ? "Dr." : "Dra."} {medico?.nome}</div>
              <div><strong>Data:</strong> {formatDate()}</div>
              <div><strong>Hora:</strong> {consulta?.horaConsulta}</div>
            </div>
            
            <div className="whatsapp-contact-section">
              <p className="whatsapp-info-text">
                Qualquer dúvida entre em contato com o nosso WhatsApp:
              </p>
              <p className="whatsapp-number">
                <i className="fab fa-whatsapp"></i> {clinicaConfig.whatsapp.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4')}
              </p>
              <button className="btn whatsapp-btn" onClick={handleWhatsAppContact}>
                <i className="fab fa-whatsapp"></i>
                Enviar Mensagem
              </button>
            </div>

            <button className="btn confirm success-close-btn" onClick={handleCloseSuccess}>
              Fechar
            </button>
          </div>
        )}
      </ReactModal>
    </>
  );
};

export default AgendarConsultaModal;
